import { laserficheIntegrationRepo } from "@/repositories/laserficheIntegrationRepo";
import type { LaserficheInspectDirectoryItem } from "@repo/types";
import { Button } from "@repo/ui/components/Base/Button";
import { ExpandableDirectoryTree } from "@repo/ui/components/Base/DirectoryTree";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";
import { useState } from "react";
import { SaveAllContext } from "./saveAllContext";
import { useBlockUnload } from "@repo/ui/components/Base/hooks/useBlockUnload";
import { firstValueFrom } from "rxjs";

export interface LaserficheDirectoryTreeProps {
  disabled?: boolean;
  title?: string;
  tenantId: string;
  teamId?: string | null;
  initialDirectory?: number | null;
  onSave?: (directoryId: number) => void | Promise<void>;
}

export const LaserficheDirectoryTree = ({
  disabled = false,
  title,
  tenantId,
  teamId = null,
  initialDirectory = null,
  onSave,
}: LaserficheDirectoryTreeProps) => {
  const [selection, setSelection] = useState<number | null>(initialDirectory);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(true);

  useBlockUnload(saved);

  const handleSave = useFutureCallback(async () => {
    if (typeof selection !== "number") return;

    setLoading(true);
    await onSave?.(selection);
    setSaved(true);
    setLoading(false);
  });

  SaveAllContext.useAsChild({
    order: 1,
    save: handleSave,
  });

  return (
    <div className="flex flex-col gap-5">
      <p className="text-lg font-bold">{title}</p>

      <div className="w-full h-96 overflow-y-auto p-4 rounded-md bg-gray-100">
        <ExpandableDirectoryTree<LaserficheInspectDirectoryItem, number>
          disabled={disabled || loading}
          entryDetails={({ name, type, id }) => ({
            label: name,
            type,
            key: "" + id,
          })}
          listDirectory={async (entry) => {
            return (
              (await firstValueFrom(
                laserficheIntegrationRepo.getDirectory(
                  tenantId,
                  teamId,
                  entry?.id,
                  { forceReload: true }
                )
              )) ?? []
            );
          }}
          showRefresh
          entrySelectionId={(entry) => entry.id}
          selection={typeof selection === "number" ? [selection] : []}
          onSelect={(_entry, _selecting, newSelection) => {
            setSelection((newSelection[0] as number) ?? null);
            setSaved(false);
          }}
          computeClassName={(component, entry) => {
            switch (component) {
              case "button.select": {
                if (entry?.id === initialDirectory) return "bg-secondary";
                break;
              }
            }
          }}
          pick="folder"
        />
      </div>

      <Button
        disabled={disabled || loading || typeof selection !== "number"}
        className="self-end"
        onClick={handleSave}
      >
        Save Destination
      </Button>
    </div>
  );
};
